import { useQuery } from '@tanstack/react-query';
import { apiGet, type ApiError } from '../lib/api';

/** The four reasons adminGate can refuse a request, with what each one means for
 *  the person who is locked out. Kept beside the data rather than in the runbook so
 *  an admin reading the screen does not need to go and look them up. */
export const REASON_HELP: Record<string, string> = {
  domain: 'Signed in with an address outside @ascera.life.',
  not_allowlisted: 'No admin_users row, or it has been revoked. Grant access to fix.',
  no_amr: 'The token carried no amr claim at all. If this appears for everyone at once, GoTrue stopped emitting it.',
  not_oauth: 'Signed in with a password rather than through an OAuth provider.',
};

export interface AccessFailureRow {
  occurred_at: string;
  actor_email: string;
  reason: string;
  url: string;
}

/** ClickHouse's default DateTime rendering: "YYYY-MM-DD HH:MM:SS[.ffffff]", no `T`,
 *  no zone. `new Date()` reads that as LOCAL time and Safari rejects it outright. */
const CLICKHOUSE_DATETIME = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(?:\.(\d+))?$/;

/** Parse `occurred_at` onto the right instant. The API already formats the column as
 *  ISO-8601 UTC; this is the belt to that brace, so a raw ClickHouse value that slips
 *  through is still treated as UTC rather than the admin's local zone. Anything
 *  unrecognised yields an Invalid Date so the caller can fall back to the raw text. */
export function parseOccurredAt(value: string): Date {
  const m = CLICKHOUSE_DATETIME.exec(value);
  if (!m) return new Date(value);
  const millis = (m[3] ?? '').padEnd(3, '0').slice(0, 3);
  return new Date(`${m[1]}T${m[2]}.${millis}Z`);
}

export interface AccessFailuresPage {
  rows: AccessFailureRow[];
}

export function useAccessFailures(hours: number) {
  return useQuery<AccessFailuresPage, ApiError>({
    queryKey: ['admin', 'access-failures', hours],
    queryFn: () =>
      apiGet('/admin/access-failures', { hours, limit: 200 }) as Promise<AccessFailuresPage>,
    // These rows live in Better Stack, not our database, and each load is a live
    // upstream call. Not retrying a 502/503 avoids hammering a third party that has
    // already said it cannot answer; the screen offers an explicit refresh instead.
    retry: false,
  });
}
